import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from './api';

// Session state loaded once at boot: who is signed in, the vocabularies, parties and offers the dialogs need,
// and the agent roster (names, colors, sigils) every byline and avatar draws from.
export type Status = { code: number; name: string; lifecycle: string; description?: string };
export type Vocab = Record<string, any[]> & { status_code: Status[] };
export type Party = { id: string; name: string; kind: string; email?: string; is_active: number; [k: string]: any };
export type Me = { user: { id: string; display_name: string; role: string; kind: string; [k: string]: any }; party?: { id: string; name: string } | null; rank: number; hostnames?: any; [k: string]: any };
export type AgentRow = {
  code: string; name: string; role_title: string; color: string; sigil: string; voice: string | null; does_md: string | null; never_md: string | null;
  team: string | null; pronouns: string | null; tagline: string | null; traits: string[]; intro: string[]; signature: string | null; signature_desc: string | null;
  color_hi: string | null; look: Record<string, unknown>; hub_status: 'on' | 'onboarding'; hub_synced_at: string | null;
  party_id: string | null; app_user_id: string | null; is_enabled: number; sort: number; skills: string[]; status: string;
  runs_today: number; cents_today: number; cents_month: number; errors_week: number; last_run_at: string | null;
  queued_items: number; drafting_items: number; awaiting_review: number; drafted_today: number; drafting_title: string | null;
  last_event?: string | null; last_event_kind?: string | null; last_event_at?: string | null; open_alerts?: number;
};

type Ctx = {
  me: Me; vocab: Vocab; parties: Party[]; offers: any[]; agents: AgentRow[]; disclosure: string; approvalMinWords: number;
  statusByCode: Map<number, Status>; agentsByCode: Map<string, AgentRow>;
  refreshParties: () => Promise<void>; refreshAgents: () => Promise<void>; refreshOffers: () => Promise<void>;
};
const AppCtx = createContext<Ctx | null>(null);

const RANK: Record<string, number> = { viewer: 0, contributor: 20, agent: 20, marketer: 30, manager: 40, admin: 50 };

export function AppProvider({ children, fallback }: { children: ReactNode; fallback: (err: string | null) => ReactNode }) {
  const [ctx, setCtx] = useState<Ctx | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const refreshParties = useCallback(async () => {
    const { parties } = await api('GET', '/parties');
    setCtx((c) => (c ? { ...c, parties } : c));
  }, []);
  const refreshOffers = useCallback(async () => {
    const { offers } = await api('GET', '/offers');
    setCtx((c) => (c ? { ...c, offers } : c));
  }, []);
  const refreshAgents = useCallback(async () => {
    const { agents, disclosure, approval_note_min_words } = await api('GET', '/agents/roster');
    setCtx((c) => (c ? { ...c, agents, disclosure, approvalMinWords: Number(approval_note_min_words ?? 8), agentsByCode: new Map((agents as AgentRow[]).map((a) => [a.code, a])) } : c));
  }, []);
  useEffect(() => {
    Promise.all([api<Me>('GET', '/me'), api<Vocab>('GET', '/vocab'), api('GET', '/parties'), api('GET', '/offers'), api('GET', '/agents/roster').catch(() => ({ agents: [], disclosure: 'AI teammate' }))])
      .then(([me, vocab, p, o, r]) => setCtx({
        me, vocab, parties: p.parties, offers: o.offers, agents: r.agents, disclosure: r.disclosure || 'AI teammate', approvalMinWords: Number(r.approval_note_min_words ?? 8),
        statusByCode: new Map(vocab.status_code.map((s) => [Number(s.code), s])), agentsByCode: new Map((r.agents as AgentRow[]).map((a) => [a.code, a])),
        refreshParties, refreshAgents, refreshOffers,
      }))
      .catch((e) => setErr(e.message));
  }, [refreshParties, refreshAgents, refreshOffers]);
  if (!ctx) return <>{fallback(err)}</>;
  return <AppCtx.Provider value={ctx}>{children}</AppCtx.Provider>;
}

export function useApp() {
  const c = useContext(AppCtx);
  if (!c) throw new Error('useApp outside AppProvider');
  const can = (role: string) => c.me.rank >= (RANK[role] ?? 99);
  const lifecycleOf = (code: unknown) => c.statusByCode.get(Number(code))?.lifecycle ?? 'new';
  const statusName = (code: unknown) => c.statusByCode.get(Number(code))?.name ?? String(code);
  return { ...c, can, lifecycleOf, statusName };
}

export const hueOf = (lc: string) => ({ new: 'plan', scheduled: 'plan', active: 'live', closing: 'close', retired: 'done' } as Record<string, string>)[lc] ?? 'done';

/** Load data for a screen; `reload` re-runs the loader (after a save). */
export function useLoad<T>(loader: () => Promise<T>, deps: unknown[]) {
  const [state, setState] = useState<{ data?: T; error?: string; loading: boolean }>({ loading: true });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    loader().then((data) => live && setState({ data, loading: false })).catch((e) => live && setState({ error: e.message, loading: false }));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { ...state, reload: () => setTick((t) => t + 1) };
}

/** First name for greetings: "Good afternoon, Kandace." */
export const firstName = (name: string) => name.split(/\s+/)[0] ?? name;
export const greeting = (d = new Date()) => { const h = d.getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'; };
